import { api } from 'src/api';
import { isMobileApp } from 'src/helpers/isMobileApp';
import { TrackerType } from './tracking/enums';

export type PlatformType = 'mobileApp' | 'mobileWeb' | 'web';

export interface TrackingMetadata {
  page?: string;
  route?: string;
  placement?: string;
  surface?: string;
  flow?: string;
  step?: string;
  element?: string;
  action?: string;
  user_id?: string;
  token?: string;
  partner_id?: string;
  timestamp?: string;
  platform?: string;
  browser?: string;
  version?: string;
  campaign_id?: string;
  experiment_id?: string;
  variant?: string;
  [key: string]: any;
}

export interface TrackingEventPayload {
  evento: string;
  produto: string;
  metadados: TrackingMetadata;
  trackers?: TrackerType[];
}

class TrackingService {
  private getBrowser(): string {
    if (typeof window === 'undefined' || !window.navigator) {
      return 'unknown';
    }

    const userAgent = window.navigator.userAgent.toLowerCase();

    if (userAgent.includes('chrome') && !userAgent.includes('edg')) {
      return 'chrome';
    }
    if (userAgent.includes('firefox')) {
      return 'firefox';
    }
    if (userAgent.includes('safari') && !userAgent.includes('chrome')) {
      return 'safari';
    }
    if (userAgent.includes('edg')) {
      return 'edge';
    }

    return 'unknown';
  }

  private getPlatformName(): string {
    if (typeof window === 'undefined' || !window.navigator) {
      return 'unknown';
    }

    const platform = window.navigator.platform.toLowerCase();

    if (platform.includes('win')) {
      return 'windows';
    }
    if (platform.includes('mac')) {
      return 'macos';
    }
    if (platform.includes('linux')) {
      return 'linux';
    }
    if (platform.includes('android')) {
      return 'android';
    }
    if (platform.includes('iphone') || platform.includes('ipad')) {
      return 'ios';
    }

    return 'unknown';
  }

  private getTimestamp(): string {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');

    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
  }

  private getVersion(): string {
    return process.env.REACT_APP_VERSION || '';
  }

  private getCurrentRoute(): string {
    if (typeof window === 'undefined') {
      return '';
    }
    return window.location.pathname + window.location.search;
  }

  async trackEvent(
    evento: string,
    produto: string,
    customMetadata?: Partial<TrackingMetadata>,
    trackers?: TrackerType[]
  ): Promise<void> {
    try {
      const metadados: TrackingMetadata = {
        page: customMetadata?.page || document.title || '',
        route: customMetadata?.route || this.getCurrentRoute(),
        placement: customMetadata?.placement || 'main',
        surface: customMetadata?.surface || 'app',
        flow: customMetadata?.flow || '',
        step: customMetadata?.step || '',
        element: customMetadata?.element || '',
        action: customMetadata?.action || 'click',
        user_id: customMetadata?.user_id || '',
        token: customMetadata?.token || '',
        partner_id: customMetadata?.partner_id || '331',
        timestamp: this.getTimestamp(),
        platform: this.getPlatformName(),
        browser: this.getBrowser(),
        version: this.getVersion(),
        campaign_id: customMetadata?.campaign_id || '',
        experiment_id: customMetadata?.experiment_id || '',
        variant: customMetadata?.variant || '',
        ...customMetadata,
      };

      const payload: TrackingEventPayload = {
        evento,
        produto,
        metadados,
        trackers: trackers && trackers.length > 0 ? trackers : undefined,
      };

      await api.post('/tracking/event', payload);
    } catch (error) {
      // Silently fail tracking to not break user experience
      if (process.env.NODE_ENV === 'development') {
        console.error('[Tracking Error]', error);
      }
    }
  }
}

export const trackingService = new TrackingService();
